// UploadStep.jsx — document intake for a new builder session. Two first-class
// sources: a file (PDF / image / .docx) via drag-drop or browse, and a paste
// area for dropping raw form text in directly (the cheapest path — no
// rendering, no OCR).

import { useRef, useState } from 'react';
import { T } from '../../theme';
import { Button } from '../fmxkit';
import { PAGE_CAP } from '../../fieldBuilder/extraction';

const ACCEPT = '.pdf,.png,.jpg,.jpeg,.webp,.gif,.docx';

export default function UploadStep({ onSource, onBack }) {
  const fileRef = useRef();
  const [dragOver, setDragOver] = useState(false);
  const [pasted, setPasted] = useState('');

  const submitFile = (file) => { if (file) onSource({ file }); };

  return (
    <div style={{ background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusCard, padding: 24 }}>
      <div style={{ font: `700 18px/24px ${T.fontSans}`, color: T.ink, marginBottom: 3 }}>New form document</div>
      <div style={{ font: `400 13px/19px ${T.fontSans}`, color: T.inkMuted, marginBottom: 18 }}>
        Upload the document that represents the form. Scans and phone photos are fine — pages are
        read visually. Up to {PAGE_CAP} pages.
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); submitFile(e.dataTransfer.files?.[0]); }}
        onClick={() => fileRef.current?.click()}
        style={{
          border: `2px dashed ${dragOver ? T.brand : T.border}`,
          background: dragOver ? T.blue50 : T.bg,
          borderRadius: T.radiusCard, padding: '38px 20px', textAlign: 'center', cursor: 'pointer',
          transition: 'all 0.15s ease',
        }}
      >
        <i className="fa-solid fa-file-arrow-up" style={{ fontSize: 26, color: T.brand }} />
        <div style={{ font: `600 14px/20px ${T.fontSans}`, color: T.ink, marginTop: 10 }}>
          Drop the form here, or click to browse
        </div>
        <div style={{ font: `400 12px/17px ${T.fontSans}`, color: T.inkSoft, marginTop: 4 }}>
          PDF · PNG · JPG · WebP · GIF · Word (.docx)
        </div>
        <input ref={fileRef} type="file" accept={ACCEPT} style={{ display: 'none' }}
          onChange={(e) => { submitFile(e.target.files?.[0]); e.target.value = ''; }} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '18px 0 10px' }}>
        <div style={{ flex: 1, height: 1, background: T.borderSubtle }} />
        <span style={{ font: `600 12px/16px ${T.fontSans}`, color: T.inkSoft }}>or paste the form text</span>
        <div style={{ flex: 1, height: 1, background: T.borderSubtle }} />
      </div>

      <textarea
        className="fmx-textarea"
        rows={7}
        placeholder={'Paste the form’s text here — questions, blanks, option lists…\ne.g.\nType of leak (check one):  [ ] Water  [ ] Gas  [ ] Other: ____\nDescribe the problem: ________________'}
        value={pasted}
        onChange={(e) => setPasted(e.target.value)}
      />
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 14 }}>
        <button className="fmx-btn-nav-back" onClick={onBack}>Back to sessions</button>
        <Button variant="primary" icon="align-left" disabled={!pasted.trim()}
          onClick={() => onSource({ pastedText: pasted })}>
          Use pasted text
        </Button>
      </div>
    </div>
  );
}
